import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useV3Api } from '../../api/useV3Api';
import { errorText } from '../../api/errorText';
import { SEVERITIES, actionText, gapText, severityClass, whenText } from '../../drift/findingModel';
import { useTableView } from '../../drift/useTableView';
import ColumnHeads from './ColumnHeads';
import JsonEditor, { jsonText } from '../JsonEditor';
import ShowCommand from '../ShowCommand';
import HardwareTab from './HardwareTab';
import SoftwareTab from './SoftwareTab';

const FACTS = 'v3/facts';
const TABS = [['hardware', 'Hardware'], ['software', 'Software'], ['findings', 'Findings']];

const COLUMNS = [
  { key: 'severity', label: 'Severity', text: (f) => f.severity, sort: (f) => SEVERITIES.indexOf(f.severity), options: true, order: SEVERITIES },
  { key: 'capability', label: 'Capability', text: (f) => f.capability, options: true },
  { key: 'finding', label: 'Finding', text: (f) => `${gapText(f.gap)} ${f.subjectName || ''}`.trim() },
  { key: 'observed', label: 'Observed', text: (f) => f.observed },
  { key: 'action', label: 'Action', text: (f) => actionText(f), options: true },
  { key: 'seen', label: 'Last seen', text: (f) => whenText(f.lastSeenUtc), sort: (f) => f.lastSeenUtc },
];

// A device opened in place, in three tabs: Hardware, Software and Findings. Findings: its labels (which decide the policies that apply), its open findings and, on request, the facts VES
// holds for it (installed programs and patch status) as read only JSON; those are not read until asked for: they are a lot of data. Hardware: the computer
// and its replacement assessment. Software: what is installed, with Uninstall on each program and Add software for this machine.
export default function DeviceView({ device, account, findings }) {
  const { get } = useV3Api();
  const [tab, setTab] = useState('hardware');
  const [facts, setFacts] = useState(null);                  // null until asked for, then { loading, item, error }
  const labels = Object.keys(device.labels || {}).sort();
  const view = useTableView(findings, COLUMNS);

  const load = async () => {
    setFacts({ loading: true, item: null, error: '' });
    try {
      const body = await get(FACTS, { account, device: device.hostname || device.deviceId });
      setFacts({ loading: false, item: ((body && body.items) || [])[0] || null, error: '' });
    } catch (ex) {
      setFacts({ loading: false, item: null, error: errorText(ex) });
    }
  };

  const shown = facts && facts.item ? { software: facts.item.software, patch: facts.item.patch } : null;
  return (
    <div className="device-view" role="group" aria-label={`${device.hostname} device`}>
      <div className="tabs device-tabs" role="tablist" aria-label="Device">
        {TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" className={`tab${tab === id ? ' active' : ''}`} aria-selected={tab === id} onClick={() => setTab(id)}>{label}</button>
        ))}
      </div>
      {tab === 'hardware' && <HardwareTab device={device} account={account} />}
      {tab === 'software' && <SoftwareTab device={device} account={account} findings={findings} />}
      {tab === 'findings' && (
      <>
      <dl className="kv">
        <dt>Device id</dt><dd className="mono">{device.deviceId}</dd>
        <dt>Labels</dt>
        <dd>{labels.length ? labels.map((l) => <span className="tag" key={l}>{`${l} is ${device.labels[l]}`}</span>) : '-'}</dd>
      </dl>

      <div className="field">
        <div className="label">Open findings ({findings.length})</div>
        {findings.length === 0 ? <div className="help">None.</div> : (
          <div className="mini-wrap">
            <table className="mini" aria-label="Findings of this device">
              <colgroup><col style={{ width: 96 }} /><col style={{ width: 96 }} /><col style={{ width: '22%' }} /><col /><col style={{ width: 150 }} /><col style={{ width: 120 }} /></colgroup>
              <thead><ColumnHeads view={view} /></thead>
              <tbody>
                {view.rows.length === 0 && <tr><td colSpan={6} className="empty">No findings match the column filters.</td></tr>}
                {view.rows.map((f) => (
                  <tr key={f.id}>
                    <td><span className={`pill ${severityClass(f.severity)}`}>{f.severity}</span></td>
                    <td>{f.capability}</td>
                    <td>{gapText(f.gap)}<div className="sub">{f.subjectName}</div></td>
                    <td className="wrap">{f.observed}</td>
                    <td>{actionText(f)}</td>
                    <td className="sub nowrap">{whenText(f.lastSeenUtc)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="field">
        <div className="label">Facts</div>
        {facts === null && <div className="help">The installed programs and patch status VES holds for this machine, which the evaluators judge.</div>}
        {facts && facts.loading && <div className="help">Loading...</div>}
        {facts && facts.error && <div className="field-error" role="alert">{facts.error}</div>}
        {facts && !facts.loading && !facts.error && !shown && <div className="help">VES has no result for this machine.</div>}
        {shown && (
          <>
            <div className="facts-note">
              {`Software reported ${whenText(shown.software && shown.software.updatedUtc) || 'never'}; patch status reported ${whenText(shown.patch && shown.patch.updatedUtc) || 'never'}.`}
            </div>
            <JsonEditor value={jsonText(shown)} readOnly label="Facts" maxHeight={520} />
          </>
        )}
      </div>

      <div className="editor-actions">
        <button type="button" className="btn btn-primary btn-sm" disabled={facts !== null && facts.loading} onClick={load}>{facts === null ? 'Show facts' : 'Reload facts'}</button>
        <Link className="btn btn-sm" to={`/v3/findings?account=${encodeURIComponent(account)}`}>All findings</Link>
        <ShowCommand route={FACTS} params={{ account, device: device.hostname || device.deviceId }} label="PowerShell" />
      </div>
      </>
      )}
    </div>
  );
}
